import { DataTypeCoaching } from '@efdigital/ll-addon-data/data-types';
import type {
  CoachProfile,
  CoachProfileLike,
  CoachProfileStatus,
} from '@efdigital/ll-addon-data/types/coaching/coach-profile';
import { jsonApiOptions } from '@efdigital/ll-admin-v2/lib/json-api';
import type { FindRecordOptions } from '@warp-drive/core/types/request';
import {
  deleteRecord,
  findRecord,
  query,
} from '@warp-drive/utilities/json-api';

import { page } from '../../json-api.ts';

// Without an explicit limit the API returns only its default first page (~48),
// so the browse list silently truncated. Every profile (published and its
// pending_review counterpart) must load for the list + approval grouping —
// keep this comfortably above the coach count, allowing for one pending
// record per coach.
const COACH_PROFILE_PAGE_LIMIT = 500;

// Picklist group keys used by coach profile fields.
export const COACHING_SPECIALISMS_GROUP = 'coaching-specialisms';
export const COACHING_COUNTRIES_GROUP = 'coaching-countries';
export const COACHING_TYPES_GROUP = 'coaching-types';
export const COACHING_INDUSTRIES_GROUP = 'coaching-industries';
export const COACHING_SENIORITY_GROUP = 'coaching-seniority';

export function findAllCoachProfiles() {
  return query<CoachProfile>(
    DataTypeCoaching.CoachProfile,
    { ...page({ offset: 0, limit: COACH_PROFILE_PAGE_LIMIT }) },
    jsonApiOptions
  );
}

export function findCoachProfile(id: string) {
  return findRecord<CoachProfile>(
    DataTypeCoaching.CoachProfile,
    id,
    jsonApiOptions as FindRecordOptions
  );
}

export function queryCoachProfileByUserId(
  userId: string,
  status: CoachProfileStatus
) {
  return query<CoachProfile>(
    DataTypeCoaching.CoachProfile,
    {
      'filter[user]': userId,
      'filter[status]': status,
    },
    jsonApiOptions as FindRecordOptions
  );
}

export function deleteCoachProfile(coach: CoachProfile) {
  return deleteRecord(coach, jsonApiOptions);
}

// A user has at most two profiles (published + its pending_review draft), so
// the coach detail page finds a profile's counterpart by querying this one
// user rather than loading every coach profile just to filter client-side.
export function queryCoachProfilesByUserId(userId: string) {
  return query<CoachProfile>(
    DataTypeCoaching.CoachProfile,
    {
      'filter[user]': userId,
      ...page({ offset: 0, limit: 10 }),
    },
    jsonApiOptions
  );
}

/** Query coach profiles by user IDs. Returns at most two profiles per user. */
export function queryCoachProfilesByUserIds(userIds: string[]) {
  return query<CoachProfile>(
    DataTypeCoaching.CoachProfile,
    {
      'filter[user][$in][]': userIds,
      ...page({ offset: 0, limit: userIds.length * 2 }),
    },
    jsonApiOptions
  );
}

/** Defaults for admin "New coach" create — identity fields left blank for the modal. */
export function defaultNewCoachProfileAttributes(): Omit<
  CoachProfileLike,
  'id' | 'modified'
> {
  return {
    prefix: '',
    firstName: '',
    lastName: '',
    suffix: '',
    user: '',
    status: 'pending_review',
    bio: '',
    approachToCoaching: '',
    blurb: '',
    photo: '',
    videoEmbed: '',
    linkedInProfileLink: '',
    yearsCoached: 0,
    lifetimeSessionCount: 0,
    coacheeCapacity: 1,
    timezone: 'Europe/London',
    profileConfirmed: false,
    acceptingCoachees: true,
    specialties: [],
    locations: [],
    countries: [],
    coachingTypes: [],
    industries: [],
    levelsOfSeniority: [],
    workExperiences: [],
    education: [],
    certifications: [],
    assessments: [],
    testimonials: [],
    languages: [],
  };
}
